import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useVisitor } from "../context/VisitorAuthContext";
import { Sparkles, Check, Trophy, ArrowLeft } from "lucide-react";

const PERKS = [
  "Unlimited curios — all 100, every day",
  "Unlimited Pub Quiz across all 8 categories",
  "Host / group quiz mode for the room",
  "Full Daily archive — replay any past quiz",
  "Purist & Multiple-Choice, no daily caps",
];

export default function Club() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const { visitor, isMember, checkout, openAuth } = useVisitor();

  useEffect(() => { api.get("/club/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {}); }, []);

  const sym = (c) => ({ gbp: "£", usd: "$", eur: "€" }[(c || "").toLowerCase()] || "");

  const subscribe = async (key) => {
    if (!visitor) { openAuth(); return; }
    setBusy(key);
    try { await checkout(key); } finally { setBusy(""); }
  };

  return (
    <Layout>
      <div className="font-ui bg-white min-h-screen border-t border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/trivia" className="inline-flex items-center gap-1.5 font-ui text-sm font-bold text-slate-500 hover:text-black mb-6"><ArrowLeft className="w-4 h-4" /> Back to the Vault</Link>
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-black text-[#C5A059] rounded-full text-[11px] font-bold tracking-widest uppercase"><Trophy className="w-3 h-3" /> CurioVault Club</span>
            <h1 className="font-pub text-4xl sm:text-5xl font-extrabold text-black mt-4">Unlock the whole vault</h1>
            <p className="font-ui text-slate-600 mt-3 max-w-lg mx-auto">Free readers get a taste each day. Club members get the lot — unlimited curios, unlimited quizzes, host mode and the full archive.</p>
          </div>

          {isMember ? (
            <div data-testid="club-already-member" className="mt-10 bg-[#F3EAD6] border-2 border-[#C5A059] rounded-2xl p-8 text-center">
              <Sparkles className="w-8 h-8 text-[#A5812F] mx-auto" />
              <h2 className="font-pub text-2xl font-extrabold text-black mt-3">You're a Club member 🎉</h2>
              <p className="font-ui text-slate-600 mt-2">Everything is unlocked. Manage your subscription anytime.</p>
              <Link to="/account" data-testid="club-to-account" className="inline-block mt-5 bg-[#C5A059] hover:bg-[#B08D44] text-black font-pub font-extrabold px-6 py-3 rounded-xl border-2 border-black shadow-[0_3px_10px_rgba(26,22,21,0.14)] uppercase tracking-wide text-sm">Manage subscription</Link>
            </div>
          ) : (
            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {plans.map((p) => (
                <div key={p.lookup_key} data-testid={`plan-${p.lookup_key}`} className="bg-white border-2 border-[#E5E5E5] rounded-2xl p-7 hover:border-[#C5A059] transition-colors flex flex-col">
                  <p className="font-ui text-xs uppercase tracking-wider font-bold text-slate-400">{p.label}</p>
                  <p className="font-pub text-4xl font-extrabold text-black mt-2">{sym(p.currency)}{p.amount}<span className="font-ui text-base font-semibold text-slate-400">/{p.interval}</span></p>
                  {p.lookup_key === "club_yearly" && <p className="font-ui text-xs font-bold text-[#A5812F] mt-1">Best value — 2 months free</p>}
                  <button
                    onClick={() => subscribe(p.lookup_key)}
                    disabled={busy === p.lookup_key}
                    data-testid={`subscribe-${p.lookup_key}`}
                    className="mt-5 w-full bg-[#C5A059] hover:bg-[#B08D44] active:scale-[0.98] disabled:opacity-50 text-black font-pub font-extrabold py-3.5 rounded-xl border-2 border-black shadow-[0_3px_10px_rgba(26,22,21,0.14)] transition-all uppercase tracking-wide"
                  >
                    {busy === p.lookup_key ? "Redirecting…" : "Subscribe"}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 bg-white border border-[#E5E5E5] rounded-2xl p-6">
            <p className="font-pub font-bold text-black mb-3">Everything in the Club</p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {PERKS.map((t) => (
                <li key={t} className="flex items-center gap-2 font-ui text-sm text-slate-700"><Check className="w-4 h-4 text-[#22C55E] shrink-0" /> {t}</li>
              ))}
            </ul>
            <p className="font-ui text-xs text-slate-400 mt-4">Secure checkout by Stripe. Cancel anytime from your Account page.</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
